import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const PROJECT_IMG = "https://images.unsplash.com/photo-1772971919691-598c37fe4b81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxzdHVkZW50cyUyMGxlYXJuaW5nJTIwY29kaW5nJTIwbW9kZXJuJTIwdGVjaCUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3ODkyMTkyNDV8MA&ixlib=rb-4.1.0&q=85";

const tabKeys = ["problem", "builder", "stack", "review"];

export const ProjectShowcase = () => {
  const { t } = useLanguage();
  const [projIdx, setProjIdx] = useState(0);
  const [tab, setTab] = useState("problem");
  const project = t.projects.items[projIdx];

  return (
    <section id="projects" className="py-20 sm:py-28 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
          className="text-xs font-mono uppercase tracking-[0.22em] text-[#C85A17] font-semibold">
          {t.projects.overline}
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }}
          className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#17181A] mt-4 mb-10" data-testid="projects-heading">
          {t.projects.heading}
        </motion.h2>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="project-showcase-toggle">
          {t.projects.items.map((p, i) => (
            <button key={p.name} data-testid={`project-select-${i}`} onClick={() => setProjIdx(i)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors duration-200 ${
                projIdx === i ? "bg-[#17181A] border-[#17181A] text-[#FDFCF7]" : "border-[#E7DFD5] text-[#5F6368] hover:border-[#17181A] hover:text-[#17181A] bg-white"
              }`}>
              {p.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <motion.div layout className="lg:col-span-5 relative overflow-hidden rounded-2xl border border-[#E7DFD5] min-h-[280px]">
            <img src={PROJECT_IMG} alt="Student building an AI project" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#121516]/80 via-[#121516]/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <AnimatePresence mode="wait">
                <motion.div key={project.name} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
                  <span className="text-xs font-mono uppercase tracking-widest text-amber-400">{project.tag}</span>
                  <h3 className="font-display text-2xl font-semibold text-[#FDFCF7] mt-1" data-testid="project-name">{project.name}</h3>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          <div className="lg:col-span-7 rounded-2xl border border-[#E7DFD5] bg-white p-7 sm:p-10 flex flex-col">
            <div className="flex flex-wrap gap-2 mb-7" role="tablist">
              {tabKeys.map((k) => (
                <button key={k} role="tab" aria-selected={tab === k} data-testid={`project-tab-${k}`} onClick={() => setTab(k)}
                  className={`rounded-full px-4 py-2 text-xs sm:text-sm font-semibold border transition-colors duration-200 ${
                    tab === k ? "bg-[#C85A17] border-[#C85A17] text-[#FDFCF7]" : "border-[#E7DFD5] text-[#5F6368] hover:border-[#C85A17] hover:text-[#C85A17]"
                  }`}>
                  {t.projects.tabs[k]}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={`${projIdx}-${tab}-${t.projects.heading}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="flex-1" data-testid="project-detail-panel">
                {tab === "stack" ? (
                  <div className="flex flex-wrap gap-2.5">
                    {project.stack.map((s) => (
                      <span key={s} className="text-sm font-mono border border-[#E7DFD5] bg-[#FAF7F2] rounded-full px-4 py-2 text-[#17181A]">{s}</span>
                    ))}
                  </div>
                ) : tab === "review" ? (
                  <div className="relative pl-8">
                    <Quote className="absolute left-0 top-0 w-5 h-5 text-[#C85A17]" />
                    <p className="font-serif text-lg sm:text-xl leading-relaxed text-[#17181A]">{project.review}</p>
                  </div>
                ) : (
                  <p className="text-base sm:text-lg leading-relaxed text-[#5F6368]">{project[tab]}</p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
